// src/pages/admin/Page_REVI/Page_REVI_Instituciones.js
//
// Tab "Instituciones" del panel admin /admin/revi. Permite al rol admin
// listar todas las institutionrevis del sistema (con filtros por nombre,
// partner y estado), crear nuevas, editarlas, activarlas/desactivarlas y
// eliminarlas. El backend protege GET /institutionrevi/all con
// requireAdmin (servidor mismo controller). Las altas reutilizan el
// controller existente createInstitutionRevi que crea el user companion
// con role:"institution" (alineado con resto del sistema REVI).

import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Space, message } from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { InstitutionsReviApi, AdminUsersV2, PlanesAPI } from "../../../api";
import { useAuth } from "../../../hooks";
import {
  InstitutionsReviTable,
  InstitutionsReviFilters,
  InstitutionReviFormModal,
} from "../../../components/Admin/InstitutionsReviCmp";

const api = new InstitutionsReviApi();
const usersApi = new AdminUsersV2();
const plansApi = new PlanesAPI();
const PAGE_SIZE = 20;

function useInstitutions(token, { search, partner, active, page, reloadTick }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await api.listAll(token, {
          search,
          partner,
          active,
          page,
          limit: PAGE_SIZE,
        });
        if (!alive) return;
        setItems(res.institutions || []);
        setTotal(res.pagination?.totalDocs || 0);
      } catch (e) {
        if (alive) {
          message.error(e?.body?.message || e.message || "Error cargando");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, search, partner, active, page, reloadTick]);

  return { items, total, loading };
}

function usePartners(token) {
  const [partners, setPartners] = useState([]);
  useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await usersApi.list(token, {
          roles: ["partner"],
          limit: 100,
        });
        if (alive) setPartners(res.users || []);
      } catch (e) {
        // Silencioso: el form se puede usar sin partners pero el admin
        // veria el select vacio. Loggeamos para detectar.
        console.error("Error cargando partners:", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);
  return partners;
}

function usePlans(token) {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await plansApi.getPlans(token, { page: 1, limit: 100 });
        if (alive) setPlans(res.plans || []);
      } catch (e) {
        console.error("Error cargando planes:", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);
  return plans;
}

export function Page_REVI_Instituciones() {
  const { accessToken } = useAuth();
  const [filters, setFilters] = useState({
    search: "",
    partner: "",
    active: "",
  });
  const [page, setPage] = useState(1);
  const [reloadTick, setReloadTick] = useState(0);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { items, total, loading } = useInstitutions(accessToken, {
    ...filters,
    page,
    reloadTick,
  });
  const partners = usePartners(accessToken);
  const plans = usePlans(accessToken);

  const handleFilterChange = useCallback((patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  }, []);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const reload = () => setReloadTick((t) => t + 1);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (editing) {
        await api.update(accessToken, editing._id, values);
        message.success("Institucion actualizada");
      } else {
        await api.create(accessToken, values);
        message.success("Institucion creada. Se envio email de bienvenida.");
      }
      closeModal();
      reload();
    } catch (e) {
      message.error(e?.body?.message || e.message || "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (row, nextActive) => {
    try {
      await api.toggleActive(accessToken, row._id, nextActive);
      message.success(`Institucion ${nextActive ? "activada" : "desactivada"}`);
      reload();
    } catch (e) {
      message.error(e?.body?.message || e.message || "Error al actualizar");
    }
  };

  const handleDelete = async (row) => {
    try {
      await api.remove(accessToken, row._id);
      message.success("Institucion eliminada");
      reload();
    } catch (e) {
      message.error(e?.body?.message || e.message || "Error al eliminar");
    }
  };

  return (
    <Card
      title="Instituciones REVI"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={reload}>
            Recargar
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nueva institucion
          </Button>
        </Space>
      }
    >
      <InstitutionsReviFilters
        search={filters.search}
        partner={filters.partner}
        active={filters.active}
        partners={partners}
        onChange={handleFilterChange}
      />
      <InstitutionsReviTable
        items={items}
        loading={loading}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
        onEdit={openEdit}
        onToggleActive={handleToggle}
        onDelete={handleDelete}
      />
      <InstitutionReviFormModal
        open={modalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        institution={editing}
        partners={partners}
        plans={plans}
        submitting={submitting}
      />
    </Card>
  );
}
